import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { ECookieKeys } from '@aca-new/app/shared/models/enums/cookie-keys.enum';
import { ELoginType } from '@aca-new/app/shared/models/enums/login-type.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { IAuthenticationData } from '@aca-new/app/shared/models/interfaces/authentication-data.interface';
import { AppLocationService } from '@aca-new/app/shared/services/browser-services/app-location/app-location-service';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { AppOverlayService } from '@aca-new/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { CookieStorageService } from '@aca-new/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { ENVIRONMENT } from '@aca-new/environments/environment';
import { Inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEmpty } from 'lodash/index';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

declare let ENV_TYPE: 'dev' | 'pp' | 'prod';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AppAuthenticationService {
  public readonly currentUserSubject$: Subject<IUser> = new Subject<IUser>();
  private _isLogout = false;

  public constructor(
    private readonly _appLocationService: AppLocationService,
    private readonly _cookieStorage: CookieStorageService,
    private readonly _storageService: StorageService,
    private readonly _httpService: HttpService,
    private readonly _overlayService: AppOverlayService,
    @Inject(WINDOW) private readonly _window: Window
  ) {}

  public updateUserData(data: Readonly<IUser>): void {
    this.currentUserSubject$.next(data);
  }

  public navigateToLogin(): void {
    if (this._isLogout) {
      this.logout();

      return;
    }

    this._storageService.setItem(EStorageKeys.PERFORMANCE_TIMINGS, performance.now().toFixed(2));

    this.navigateToMyQimaLogin();
  }

  public navigateToMyQimaLogin(): void {
    const redirectUrl = encodeURIComponent(`${location.pathname + location.search}`);

    location.assign(`/v2/login?redirect=${redirectUrl}`);
  }

  public navigateToAcaLogin(): void {
    this.clearAuthenticationData();
    const acaLoginUrl = `/login?&unauthenticated&redirect=${encodeURIComponent(`${location.origin}/login?token={token}&userId={userId}&sessionId={sessionId}&loginType={loginType}`)}`;
    let redirectUrl = `${location.pathname + location.search}`;

    if (redirectUrl.includes('/login?token')) {
      redirectUrl = '/home';
    }

    location.assign(this._appLocationService.getAcaUrl() + acaLoginUrl + encodeURIComponent(`&redirectUrl=${redirectUrl}`));
  }

  public saveAuthenticationData(authData: IAuthenticationData): void {
    const expires = /^\d+$/.test(authData.validBefore) ? new Date(+authData.validBefore * 1000) : undefined;

    this._cookieStorage.set(ECookieKeys.AUTH_TOKEN, authData.token, { expires });
    this._cookieStorage.set(ECookieKeys.USER_ID, authData.userId, { expires });
    this._cookieStorage.set(ECookieKeys.USER_NAME, authData.userName, { expires });
    this._cookieStorage.set(ECookieKeys.VALID_BEFORE, authData.validBefore, { expires });
    this._cookieStorage.set(ECookieKeys.SESSION_ID, authData.sessionId, { expires });
    this._cookieStorage.set(ECookieKeys.LOGIN_TYPE, authData.loginType, { expires });
    this._cookieStorage.set(ECookieKeys.REFRESH_KEY, authData.refreshKey, { expires });
    this._cookieStorage.set(ECookieKeys.USER_TYPE, authData.userType, { expires });
    // TODO: remove user id from session storage
    this._storageService.setItem(EStorageKeys.USER_ID, authData.userId);
  }

  /**
   * get authentication data from cookie, not session storage!
   * @returns {Readonly<Partial<IAuthenticationData>>} Authentication data from cookies.
   */
  public getAuthenticationData(): Readonly<Partial<IAuthenticationData>> {
    return {
      token: this._cookieStorage.get(ECookieKeys.AUTH_TOKEN),
      userId: this._cookieStorage.get(ECookieKeys.USER_ID),
      userName: this._cookieStorage.get(ECookieKeys.USER_NAME),
      validBefore: this._cookieStorage.get(ECookieKeys.VALID_BEFORE),
      sessionId: this._cookieStorage.get(ECookieKeys.SESSION_ID),
      loginType: this._cookieStorage.get(ECookieKeys.LOGIN_TYPE),
      refreshKey: this._cookieStorage.get(ECookieKeys.REFRESH_KEY),
      userType: this._cookieStorage.get(ECookieKeys.USER_TYPE),
    };
  }

  public clearAuthenticationData(): void {
    this._storageService.clearAuthenticationData();
    this._cookieStorage.remove(ECookieKeys.AUTH_TOKEN);
    this._cookieStorage.remove(ECookieKeys.USER_ID);
    this._cookieStorage.remove(ECookieKeys.USER_NAME);
    this._cookieStorage.remove(ECookieKeys.VALID_BEFORE);
    this._cookieStorage.remove(ECookieKeys.SESSION_ID);
    this._cookieStorage.remove(ECookieKeys.LOGIN_TYPE);
    this._cookieStorage.remove(ECookieKeys.REFRESH_KEY);
    this._cookieStorage.remove(ECookieKeys.USER_TYPE);
  }

  public logout(): void {
    this._overlayService.updateIsLoading(true);
    this.deleteToken$()
      .pipe(untilDestroyed(this))
      .subscribe((): void => {
        this.clearAuthenticationData();
        this._isLogout = true;
        this._window.location.replace(`${ENVIRONMENT[ENV_TYPE].MARKETING_WEBSITE_URL}/logout`);
      });
  }

  public isAuthenticated(): boolean {
    return !isEmpty(this._cookieStorage.get(ECookieKeys.AUTH_TOKEN));
  }

  /**
   * @returns {boolean} True when the current session was created from back office (`LoginFromSSO`).
   */
  public getIsLoginFromBackOffice(): boolean {
    return this.getAuthenticationData().loginType === ELoginType.LOGIN_FROM_SSO;
  }

  public isTokenExpired(): boolean {
    const validBefore: string = this._cookieStorage.get(ECookieKeys.VALID_BEFORE);

    if (!validBefore) {
      return false;
    }

    const now = Date.now() / 1000;

    return now >= parseInt(validBefore, 10);
  }

  public isValidSession(): boolean {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN);

    return !!userId && !!token && !this.isTokenExpired();
  }

  public getCurrentUser(): IUser {
    return JSON.parse(this._storageService.getItem(EStorageKeys.USER_DATA) || '{}');
  }

  public getCompanyId(): string {
    const userData = this.getCurrentUser();

    return userData.company?.id || '';
  }

  public deleteToken$(): Observable<void> {
    return this._httpService.httpClient
      .delete<void>(`${SERVER_URL}/auth/v2/token`, {
        observe: 'body',
      })
      .pipe(
        map((response: void): void => response),
        catchError((): Observable<void> => {
          return of<void>(undefined);
        })
      );
  }
}
